import {
  IUserData,
  IMentionAttachmentCommentData,
  IAttachmentData
} from '@/models';
import {
  IInventoryAdvancePaymentService,
  IInventoryAdvancePaymentPaginatedListData,
  IInventoryAdvancePaymentExportFilterParams,
  INVENTORY_ADVANCE_PAYMENT_EXPORT_TIMEOUT_IN_MILLISECONDS,
  IInventoryAdvancePaymentFilterParam,
  IInventoryAdvancePaymentUpdateState
} from './inventoryadvancepayment.types';
import { AxiosInstance } from 'axios';
import { objectToFormData } from '@/utils/objectToFormData';
import { Transition } from '@/models/transition';
import { IInventoryAdvancePaymentData } from '@/models/inventory-advance-payment';

export class InventoryAdvancePaymentService
  implements IInventoryAdvancePaymentService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/inventory-payments/';
  }

  // TODO: LH - 2022-12-22 - let's encapsulate these paramaters
  fetchInventoryAdvancePayments(
    filter?: IInventoryAdvancePaymentFilterParam
  ): Promise<IInventoryAdvancePaymentPaginatedListData> {
    return this.axios
      .get<IInventoryAdvancePaymentPaginatedListData>(`${this.endpoint}`, {
        timeout: 1 * (60 * 1000), //1 minute
        params: filter
      })
      .then(({ data }) => data);
  }

  fetchInventoryAdvancePayment(
    id: number
  ): Promise<IInventoryAdvancePaymentData> {
    return this.axios
      .get<IInventoryAdvancePaymentData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  createInventoryAdvancePayment(
    data: IInventoryAdvancePaymentData
  ): Promise<IInventoryAdvancePaymentData> {
    const formData = objectToFormData(data);

    return this.axios
      .post<IInventoryAdvancePaymentData>(`${this.endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  updateInventoryAdvancePayment(
    data: IInventoryAdvancePaymentData
  ): Promise<IInventoryAdvancePaymentData> {
    const formData = objectToFormData(data);

    return this.axios
      .patch<IInventoryAdvancePaymentData>(
        `${this.endpoint}${data.id}/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  deleteInventoryAdvancePayment(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`);
  }

  deleteInventoryAdvancePaymentAttachment(
    InventoryAdvancePaymentId: number,
    attachmentId: number
  ): Promise<any> {
    return this.axios
      .delete(
        `${this.endpoint}${InventoryAdvancePaymentId}/remove-attachment/${attachmentId}/`
      )
      .then(({ data }) => data);
  }

  addInventoryAdvancePaymentAttachments(
    id: number,
    attachments: File[]
  ): Promise<any> {
    const formData = new FormData();
    attachments.forEach((attachment, index) => {
      formData.append(`attachments[${index}]`, attachment);
    });

    return this.axios
      .post(`${this.endpoint}${id}/add-attachments/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  updateInventoryAdvancePaymentState(
    data: IInventoryAdvancePaymentUpdateState
  ): Promise<IInventoryAdvancePaymentData> {
    return this.axios
      .put(`${this.endpoint}${data.id}/set_state/${data.state}/`, {
        assignee: data.assignee,
        comment: data.comment
      })
      .then(({ data }) => data);
  }

  fetchCurrentAvailableAssignees(id: number): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(`${this.endpoint}${id}/current-available-assignees/`)
      .then(({ data }) => data);
  }

  fetchInventoryAdvancePaymentFinanceManagers(
    id: number
  ): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(`${this.endpoint}${id}/finance-managers/`)
      .then(({ data }) => data);
  }

  assignInventoryAdvancePaymentFinanceManager(
    id: number,
    financeManager: number
  ): Promise<IInventoryAdvancePaymentData> {
    return this.axios
      .put(`${this.endpoint}${id}/assign-finance-manager/`, {
        finance_manager: financeManager
      })
      .then(({ data }) => data);
  }

  fetchInventoryAdvancePaymentAPSpecialists(id: number): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(`${this.endpoint}${id}/ap_specialists/`)
      .then(({ data }) => data);
  }

  assignInventoryAdvancePaymentAPSpecialist(
    id: number,
    apSpecialist: number
  ): Promise<IInventoryAdvancePaymentData> {
    return this.axios
      .put(`${this.endpoint}${id}/assign_ap_specialist/`, {
        ap_specialist: apSpecialist
      })
      .then(({ data }) => data);
  }

  fetchInventoryAdvancePaymentApprovers(id: number): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(`${this.endpoint}${id}/approvers/`)
      .then(({ data }) => data);
  }

  assignInventoryAdvancePaymentApprover(
    id: number,
    approver: number
  ): Promise<IInventoryAdvancePaymentData> {
    return this.axios
      .put(`${this.endpoint}${id}/assign_approver/`, {
        approver
      })
      .then(({ data }) => data);
  }

  updateInventoryAdvancePaymentOwner(
    id: number,
    owner: number
  ): Promise<IInventoryAdvancePaymentData> {
    return this.axios
      .put(`${this.endpoint}${id}/set-owner/`, {
        owner
      })
      .then(({ data }) => data);
  }

  fetchInventoryAdvancePaymentActivities(id: number): Promise<Transition[]> {
    return this.axios
      .get<Transition[]>(`${this.endpoint}${id}/activities/`)
      .then(({ data }) => data);
  }

  // comments
  fetchInventoryAdvancePaymentComments(
    inventoryAdvancePaymentId: number
  ): Promise<IInventoryAdvancePaymentData[]> {
    return this.axios
      .get<IInventoryAdvancePaymentData[]>(
        `${this.endpoint}${inventoryAdvancePaymentId}/comments/`
      )
      .then(({ data }) => data);
  }

  fetchInventoryAdvancePaymentComment(
    inventoryAdvancePaymentId: number,
    commentId: number
  ): Promise<IInventoryAdvancePaymentData> {
    return this.axios
      .get<IInventoryAdvancePaymentData>(
        `${this.endpoint}${inventoryAdvancePaymentId}/comments/${commentId}/`
      )
      .then(({ data }) => data);
  }

  createInventoryAdvancePaymentComment(
    inventoryAdvancePaymentId: number,
    comment: IMentionAttachmentCommentData
  ): Promise<IInventoryAdvancePaymentData> {
    return this.axios
      .post<IInventoryAdvancePaymentData>(
        `${this.endpoint}${inventoryAdvancePaymentId}/comments/`,
        comment
      )
      .then(({ data }) => data);
  }

  updateInventoryAdvancePaymentComment(
    inventoryAdvancePaymentId: number,
    comment: IMentionAttachmentCommentData
  ): Promise<IInventoryAdvancePaymentData> {
    return this.axios
      .patch<IInventoryAdvancePaymentData>(
        `${this.endpoint}${inventoryAdvancePaymentId}/comments/${comment.id}/`,
        comment
      )
      .then(({ data }) => data);
  }

  deleteInventoryAdvancePaymentComment(
    inventoryAdvancePaymentId: number,
    commentId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${inventoryAdvancePaymentId}/comments/${commentId}/`
      )
      .then(({ data }) => data);
  }

  addInventoryAdvancePaymentCommentAttachments(
    inventoryAdvancePaymentId: number,
    commentId: number,
    attachments: IAttachmentData[]
  ): Promise<IMentionAttachmentCommentData> {
    const formData = objectToFormData({ attachments });
    return this.axios
      .post<IMentionAttachmentCommentData>(
        `${this.endpoint}${inventoryAdvancePaymentId}/comments/${commentId}/add-attachments/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  removeInventoryAdvancePaymentCommentAttachment(
    inventoryAdvancePaymentId: number,
    commentId: number,
    attachmentId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${inventoryAdvancePaymentId}/comments/${commentId}/remove-attachment/${attachmentId}/`
      )
      .then();
  }

  fetchInventoryAdvancePaymentCommentsMentionableUsers(
    inventoryAdvancePaymentId: number
  ): Promise<IUserData[]> {
    return this.axios
      .get(
        `${this.endpoint}${inventoryAdvancePaymentId}/comments/mentionable-users/`
      )
      .then(({ data }) => data);
  }

  exportInventoryAdvancePaymentPdf(
    inventoryAdvancePaymentId: number
  ): Promise<Blob> {
    return this.axios
      .get(`${this.endpoint}${inventoryAdvancePaymentId}/export_pdf/`, {
        responseType: 'blob'
      })
      .then(({ data }) => data);
  }

  exportInventoryAdvancePayments(
    params?: IInventoryAdvancePaymentExportFilterParams
  ): Promise<Blob> {
    return this.axios
      .get(`${this.endpoint}export-csv/`, {
        responseType: 'blob',
        timeout: INVENTORY_ADVANCE_PAYMENT_EXPORT_TIMEOUT_IN_MILLISECONDS,
        params: params
      })
      .then(({ data }) => data);
  }

  updateMultipleInventoryAdvancePaymentOwner(
    owner: number,
    requisitions: number[]
  ): Promise<void> {
    return this.axios
      .post<void>(`${this.endpoint}bulk-set-owner/`, {
        requisitions,
        owner
      })
      .then(({ data }) => data);
  }
}
